import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../auth";
import { Button } from "@/components/ui/button";
import { Plus, Globe, Trash2, ExternalLink, Settings, Sparkles, LogOut } from "lucide-react";
import { toast } from "sonner";
import OxyHeader from "../components/OxyHeader";

export default function MyWebsites() {
  const { user } = useAuth();
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) { navigate("/"); return; }
    api.get("/websites/mine").then(r => setWebsites(r.data)).finally(() => setLoading(false));
  }, [user]);

  const del = async (id) => {
    if (!confirm("Delete this website and all its data?")) return;
    await api.delete(`/websites/${id}`);
    setWebsites(websites.filter(w => w.website_id !== id));
    toast.success("Deleted");
  };

  if (!user) return null;
  return (
    <div className="min-h-screen bg-slate-50">
      <OxyHeader/>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-extrabold">My Websites</h1>
            <p className="text-slate-500 mt-1 font-bn">আপনার সব সাইট এক জায়গায়</p>
          </div>
          <Link to="/websites/new">
            <Button className="bg-sky-500 hover:bg-sky-600 rounded-full px-5" data-testid="create-website-btn">
              <Plus className="w-4 h-4 mr-1.5"/> Create Website
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-20 text-slate-400">Loading…</div>
        ) : websites.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-slate-300">
            <Globe className="w-14 h-14 mx-auto text-sky-500"/>
            <h3 className="mt-4 text-xl font-bold">No websites yet</h3>
            <p className="text-slate-500 mt-2 font-bn">শুরু করতে নতুন সাইট তৈরি করুন</p>
            <Link to="/websites/new"><Button className="mt-6 bg-sky-500 hover:bg-sky-600 rounded-full" data-testid="empty-create-btn"><Sparkles className="w-4 h-4 mr-2"/>Create your first website</Button></Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {websites.map(w => (
              <div key={w.website_id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-sky-300 transition-all" data-testid={`website-card-${w.slug}`}>
                <div className="h-32 bg-gradient-to-br from-sky-500 via-cyan-500 to-emerald-500 relative">
                  <div className="absolute top-3 left-3 bg-white/95 rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase text-slate-700">{w.type}</div>
                  <div className={`absolute top-3 right-3 rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase ${w.status === "published" ? "bg-emerald-500 text-white" : "bg-white/90 text-slate-700"}`}>{w.status}</div>
                </div>
                <div className="p-4">
                  <div className="font-bold truncate">{w.name}</div>
                  <div className="text-xs text-slate-500 truncate font-mono">{w.subdomain}</div>
                  <div className="mt-3 flex gap-2">
                    <Link to={`/websites/${w.website_id}`} className="flex-1"><Button size="sm" variant="outline" className="w-full" data-testid={`manage-${w.slug}`}><Settings className="w-3.5 h-3.5 mr-1.5"/>Manage</Button></Link>
                    <a href={`/w/${w.slug}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><ExternalLink className="w-3.5 h-3.5"/></Button></a>
                    <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => del(w.website_id)}><Trash2 className="w-3.5 h-3.5"/></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
